import { ReactNode, useEffect } from "react";

import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Center, Loader } from "@mantine/core";

import { useAuth } from "@admin/hooks/use-auth";
import { paths } from "@admin/routes/paths";
import { authController } from "@admin/utils/auth-controller";
import { refreshAuthToken } from "@admin/utils/api-helpers";
import { appConstants } from "@admin/constants/app-constants";
import { navigateToLogin } from "@admin/utils/auth-navigation";

interface AuthGuardProps {
	children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
	const { isAuthenticated, isInitialized, isCheckingAuth, isRefreshing } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	// Hard timeout to prevent indefinite loading
	useEffect(() => {
		// Use either checking state to trigger the timeout
		if (isCheckingAuth || isRefreshing) {
			const timer = setTimeout(() => {
				console.log("Auth check/refresh timed out after 5 seconds, forcing reset");
				authController.resetAuth();
			}, 5000);

			return () => clearTimeout(timer);
		}
	}, [isCheckingAuth, isRefreshing]);

	// Auth check effect - only run if not authenticated and not already checking
	useEffect(() => {
		const checkAuth = async () => {
			if (!isAuthenticated && !isCheckingAuth && !isRefreshing) {
				console.log("AuthGuard: Checking authentication status");
				try {
					// Try to refresh the token first
					const refreshSuccess = await refreshAuthToken();

					if (refreshSuccess) {
						console.log("AuthGuard: Token refreshed successfully");
					} else {
						console.log("AuthGuard: Token refresh failed, redirecting to login");
						navigateToLogin(navigate, location.pathname + location.search);
					}
				} catch (error) {
					console.error("AuthGuard: Error refreshing token:", error);
					navigateToLogin(navigate, location.pathname + location.search);
				}
			}
		};

		// Only run this check if we're fully initialized to avoid premature checks
		if (isInitialized) {
			checkAuth();
		}
	}, [isAuthenticated, isCheckingAuth, isRefreshing, isInitialized, navigate, location]);

	// If not initialized yet, show loading
	if (!isInitialized) {
		return (
			<Center style={{ height: "100vh" }}>
				<Loader size="lg" />
			</Center>
		);
	}

	// If not authenticated, redirect to login
	if (!isAuthenticated) {
		const currentPath = location.pathname + location.search;
		const encodedPath = encodeURIComponent(currentPath);
		return (
			<Navigate to={`${paths.auth.root}?${appConstants.redirectQueryParamName}=${encodedPath}`} replace />
		);
	}

	// User is authenticated, render children
	return <>{children}</>;
}
